import React, { JSX } from "react";
import { Box, TextField, Autocomplete, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SkinAnalysisAttribute } from "../../../interfaces/AssessmentInterfaces";

interface SkinAnalysisRowProps {
  attribute: SkinAnalysisAttribute;
  onRemove: () => void;
  onChange: (attribute: SkinAnalysisAttribute) => void;
  remainingAttributes: SkinAnalysisAttribute[];
}

export const defaultAttribute: SkinAnalysisAttribute = {
  name: "",
  dbKey: "",
  defaultValue: "",
  dropdownValues: [],
  reading: "",
  type: "text"
};

const SkinAnalysisRow: React.FC<SkinAnalysisRowProps> = ({
  attribute,
  onChange,
  onRemove,
  remainingAttributes
}): JSX.Element => {
  const [rowAttribute, setRowAttribute] = React.useState(attribute);

  React.useEffect(() => {
    setRowAttribute(attribute);
  }, [attribute]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        mb: 1
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Autocomplete
          freeSolo
          value={rowAttribute}
          options={remainingAttributes}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return option.name || "";
          }}
          onInputChange={(_, newValue) => {
            if (typeof rowAttribute === "object" && rowAttribute !== null) {
              setRowAttribute({
                ...rowAttribute,
                name: newValue,
                dbKey: newValue
              });
            }
          }}
          onBlur={() => onChange(rowAttribute)}
          onChange={(_, newValue) => {
            if (!newValue) {
              const updated = { ...defaultAttribute };
              setRowAttribute(updated);
              onChange(updated);
              return;
            }

            if (typeof newValue === "string") {
              const updated = {
                ...defaultAttribute,
                name: newValue,
                dbKey: newValue,
                type: "text"
              };
              setRowAttribute(updated);
              onChange(updated);
            } else {
              setRowAttribute(newValue);
              onChange(newValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              fullWidth
              size="small"
              placeholder="Select or type field"
            />
          )}
        />
      </Box>

      <Box sx={{ flex: 1 }}>
        {rowAttribute.dropdownValues && rowAttribute.dropdownValues.length > 0 ? (
          <Autocomplete
            freeSolo
            inputValue={rowAttribute.reading}
            onInputChange={(_, newValue) => {
              setRowAttribute({ ...rowAttribute, reading: newValue || "" });
            }}
            value={rowAttribute.reading}
            onBlur={() => onChange(rowAttribute)}
            onChange={(_, newValue) => {
              setRowAttribute({ ...rowAttribute, reading: (newValue as string) || "" });
            }}
            options={rowAttribute.dropdownValues}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Reading"
              />
            )}
          />
        ) : (
          <TextField
            variant="outlined"
            size="small"
            placeholder="Reading"
            fullWidth
            value={rowAttribute.reading}
            onChange={(e) => setRowAttribute({ ...rowAttribute, reading: e.target.value })}
            onBlur={() => onChange(rowAttribute)}
          />
        )}
      </Box>
      <IconButton onClick={onRemove} size="small">
        <CloseIcon />
      </IconButton>
    </Box>
  );
};

export default SkinAnalysisRow;
